/**
 * Pod thread inbox list: every pod thread the viewer can see, across all their pods, newest first.
 *
 * Used by students, guardians, and tutors alike. The pod name is always shown because a viewer can
 * belong to more than one pod, and the safeguarding notice is always visible: there are no private
 * tutor-student channels inside a pod.
 */
import { MessageSquare, Users, Eye, Plus, Loader2 } from 'lucide-react'

const AUDIENCE_LABEL = {
  cohort: 'Whole cohort',
  group: 'Group',
  class: 'Class',
  students: 'Selected students'
}

const fmtTime = (iso) => {
  if (!iso) return ''
  const d = new Date(iso)
  const today = new Date()
  const sameDay = d.toDateString() === today.toDateString()
  return sameDay
    ? d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : d.toLocaleDateString()
}

export default function PodThreadList({
  threads,
  loading,
  selectedThreadId,
  onSelectThread,
  onCompose,
  canCompose
}) {
  return (
    <div className="flex h-full flex-col border-r border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
      <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 dark:border-gray-700">
        <h2 className="flex items-center gap-2 font-semibold text-gray-900 dark:text-gray-100">
          <Users className="h-5 w-5 text-indigo-500" />
          Pod messages
        </h2>
        {canCompose && (
          <button
            type="button"
            onClick={onCompose}
            className="inline-flex items-center gap-1 rounded bg-indigo-600 px-2.5 py-1.5 text-sm font-medium text-white hover:bg-indigo-700"
          >
            <Plus className="h-4 w-4" />
            New
          </button>
        )}
      </div>

      <p className="flex items-start gap-1.5 border-b border-indigo-100 bg-indigo-50 px-4 py-2 text-xs text-indigo-900 dark:border-indigo-900/40 dark:bg-indigo-950/30 dark:text-indigo-200">
        <Eye className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>Messages in a pod are visible to that pod&apos;s whole tutor team.</span>
      </p>

      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center gap-2 p-4 text-sm text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading
          </div>
        ) : threads.length === 0 ? (
          <p className="p-4 text-sm text-gray-500 dark:text-gray-400">No pod conversations yet.</p>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-gray-800">
            {threads.map((t) => (
              <li key={t.id}>
                <button
                  type="button"
                  onClick={() => onSelectThread(t.id)}
                  className={`flex w-full items-start gap-2 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800 ${
                    selectedThreadId === t.id ? 'bg-indigo-50 dark:bg-indigo-950/30' : ''
                  }`}
                >
                  <MessageSquare className="mt-0.5 h-4 w-4 shrink-0 text-indigo-400" />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center justify-between gap-2">
                      <span
                        className={`truncate text-sm ${
                          t.unreadCount > 0
                            ? 'font-semibold text-gray-900 dark:text-gray-100'
                            : 'text-gray-800 dark:text-gray-200'
                        }`}
                      >
                        {t.subject || 'Pod conversation'}
                      </span>
                      <span className="shrink-0 text-xs text-gray-400">
                        {fmtTime(t.lastMessageAt)}
                      </span>
                    </span>
                    <span className="mt-0.5 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                      <span className="truncate">{t.organizationName}</span>
                      <span aria-hidden="true">·</span>
                      <span className="shrink-0">
                        {(t.audienceType === 'group' && t.groupName) ||
                          (t.audienceType === 'class' && t.className) ||
                          AUDIENCE_LABEL[t.audienceType] ||
                          t.audienceType}
                      </span>
                      {t.unreadCount > 0 && (
                        <span className="ml-auto shrink-0 rounded-full bg-indigo-600 px-2 py-0.5 text-[11px] font-medium text-white">
                          {t.unreadCount}
                        </span>
                      )}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
